/**
 * DataCache — In-memory caching layer for the Reporting Portal.
 * 
 * Wraps the existing DatabaseService transparently, providing:
 * - Cache-hit (within stale time → return data, no fetch)
 * - Cache-miss (no entry → fetch, store, return)
 * - Stale-while-revalidate (expired → return stale data, trigger background refresh)
 * - LRU eviction when memory limit exceeded
 * - Request deduplication for concurrent identical requests
 * 
 * @module dataCache
 */

/**
 * Generate a stable hash string from a params object.
 * Produces a consistent key regardless of property insertion order.
 * @param {Object} params
 * @returns {string}
 */
function stableHash(params) {
  if (params === null || params === undefined) return '';
  if (typeof params !== 'object') return String(params);

  const str = JSON.stringify(params, Object.keys(params).sort());
  let hash = 0;
  for (let i = 0; i < str.length; i++) {
    const char = str.charCodeAt(i);
    hash = ((hash << 5) - hash) + char;
    hash = hash & hash; // Convert to 32-bit integer
  }
  return Math.abs(hash).toString(36);
}

/**
 * @typedef {Object} CacheEntry
 * @property {string} key - Composite key (collection:operation:hash)
 * @property {any} data - Cached response data
 * @property {number} fetchedAt - Timestamp when data was fetched
 * @property {number} lastAccessedAt - Timestamp of last access (for LRU)
 * @property {number} size - Approximate size in bytes
 * @property {number} staleTime - Stale time override for this entry
 */

const ONE_MINUTE = 60 * 1000;
const ONE_HOUR = 60 * ONE_MINUTE;
const ONE_DAY = 24 * ONE_HOUR;

const DEFAULT_OPTIONS = {
  maxSizeBytes: 50 * 1024 * 1024,       // 50MB
  defaultStaleTime: 5 * ONE_MINUTE,      // 5 minutes
  itemsStaleTime: ONE_DAY,               // 24 hours
  normsStaleTime: ONE_DAY,               // 24 hours
  surveyDataStaleTime: ONE_HOUR          // 1 hour
};

class DataCache {
  /**
   * @param {Object} [options]
   * @param {number} [options.maxSizeBytes=52428800] - Maximum total cache size before LRU eviction
   * @param {number} [options.defaultStaleTime=300000] - Default stale time (5 min)
   * @param {number} [options.itemsStaleTime=86400000] - Stale time for Items collection (24h)
   * @param {number} [options.normsStaleTime=86400000] - Stale time for Norms collection (24h)
   * @param {number} [options.surveyDataStaleTime=3600000] - Stale time for survey data (1h)
   */
  constructor(options = {}) {
    this.options = { ...DEFAULT_OPTIONS, ...options };

    /** @type {Map<string, CacheEntry>} */
    this._cache = new Map();

    /** @type {Map<string, { promise: Promise, callerCount: number, startedAt: number }>} */
    this._inFlight = new Map();

    /** @type {number} Total size of all cached entries in bytes */
    this._totalSize = 0;

    /** @type {number} Cache hit counter */
    this._hits = 0;

    /** @type {number} Cache miss counter */
    this._misses = 0;

    /** @type {string|null} Date string when cache was last validated */
    this._cacheDate = new Date().toLocaleDateString();
  }

  /**
   * Generate a composite cache key.
   * @param {string} collection
   * @param {string} operation
   * @param {Object} params
   * @returns {string}
   */
  _generateKey(collection, operation, params) {
    return `${collection}:${operation}:${stableHash(params)}`;
  }

  /**
   * Calculate approximate byte size of data (UTF-16 encoding).
   * @param {any} data
   * @returns {number}
   */
  _calculateSize(data) {
    try {
      return JSON.stringify(data).length * 2;
    } catch {
      return 0;
    }
  }

  /**
   * Determine the stale time for a given collection.
   * @param {string} collection
   * @param {Object} [options]
   * @returns {number}
   */
  _getStaleTime(collection, options = {}) {
    if (options.staleTime !== undefined) return options.staleTime;

    const collectionLower = collection.toLowerCase();
    if (collectionLower === 'items') return this.options.itemsStaleTime;
    if (collectionLower === 'norms') return this.options.normsStaleTime;
    if (collectionLower === 'responses' || collectionLower === 'surveys') {
      return this.options.surveyDataStaleTime;
    }
    return this.options.defaultStaleTime;
  }

  /**
   * Check if the cache date has changed (new calendar day).
   * If so, invalidate survey/response data.
   * @private
   */
  _checkDateInvalidation() {
    const today = new Date().toLocaleDateString();
    if (this._cacheDate !== today) {
      this._cacheDate = today;
      // Invalidate survey and response data on new day
      this.clearCollection('Responses');
      this.clearCollection('Surveys');
    }
  }

  /**
   * Evict least-recently-used entries until total size is under the limit.
   * @private
   */
  _evict() {
    while (this._totalSize > this.options.maxSizeBytes && this._cache.size > 0) {
      // Find the entry with the oldest lastAccessedAt
      let oldestKey = null;
      let oldestTime = Infinity;

      for (const [key, entry] of this._cache) {
        if (entry.lastAccessedAt < oldestTime) {
          oldestTime = entry.lastAccessedAt;
          oldestKey = key;
        }
      }

      if (oldestKey) {
        const entry = this._cache.get(oldestKey);
        this._totalSize -= entry.size;
        this._cache.delete(oldestKey);
      } else {
        break;
      }
    }
  }

  /**
   * Store data in cache manually (for pre-populated data from login flow).
   * @param {string} collection - Collection name
   * @param {string} operation - Operation type
   * @param {Object} params - Query parameters
   * @param {any} data - Data to cache
   * @param {Object} [options] - { staleTime }
   */
  set(collection, operation, params, data, options = {}) {
    const key = this._generateKey(collection, operation, params);
    const size = this._calculateSize(data);
    const staleTime = this._getStaleTime(collection, options);

    // Remove existing entry if present (to update size tracking)
    if (this._cache.has(key)) {
      const existing = this._cache.get(key);
      this._totalSize -= existing.size;
    }

    const now = Date.now();
    /** @type {CacheEntry} */
    const entry = {
      key,
      data,
      fetchedAt: now,
      lastAccessedAt: now,
      size,
      staleTime
    };

    this._cache.set(key, entry);
    this._totalSize += size;

    // Evict if over size limit
    this._evict();
  }

  /**
   * Get data from cache or fetch from API.
   * Implements cache-hit, cache-miss, and stale-while-revalidate patterns.
   * 
   * @param {string} collection - Collection name
   * @param {string} operation - Operation type (read, report-data, etc.)
   * @param {Object} params - Query parameters
   * @param {Function} fetchFn - Async function to call on cache miss
   * @param {Object} [options] - { staleTime, forceRefresh }
   * @returns {Promise<any>} Resolved data
   */
  async get(collection, operation, params, fetchFn, options = {}) {
    this._checkDateInvalidation();

    const key = this._generateKey(collection, operation, params);
    const staleTime = this._getStaleTime(collection, options);

    // Force refresh bypasses cache entirely
    if (!options.forceRefresh) {
      const entry = this._cache.get(key);

      if (entry) {
        const age = Date.now() - entry.fetchedAt;
        entry.lastAccessedAt = Date.now();

        if (age <= staleTime) {
          // Cache hit — data is fresh
          this._hits++;
          return entry.data;
        } else {
          // Stale-while-revalidate — return stale data, trigger background refresh
          this._hits++;
          this._backgroundRefresh(key, collection, operation, params, fetchFn, options);
          return entry.data;
        }
      }
    }

    // Cache miss
    this._misses++;

    // Check if there's already an in-flight request for this key
    if (this._inFlight.has(key)) {
      const inflight = this._inFlight.get(key);
      inflight.callerCount++;
      return inflight.promise;
    }

    // Create new fetch and register in-flight
    const promise = this._fetchAndStore(key, collection, operation, params, fetchFn, options);
    this._inFlight.set(key, {
      promise,
      callerCount: 1,
      startedAt: Date.now()
    });

    try {
      const result = await promise;
      return result;
    } finally {
      this._inFlight.delete(key);
    }
  }

  /**
   * Fetch data and store in cache.
   * @private
   */
  async _fetchAndStore(key, collection, operation, params, fetchFn, options = {}) {
    const data = await fetchFn();
    const size = this._calculateSize(data);
    const staleTime = this._getStaleTime(collection, options);

    const now = Date.now();
    const entry = {
      key,
      data,
      fetchedAt: now,
      lastAccessedAt: now,
      size,
      staleTime
    };

    // Remove existing entry size tracking
    if (this._cache.has(key)) {
      const existing = this._cache.get(key);
      this._totalSize -= existing.size;
    }

    this._cache.set(key, entry);
    this._totalSize += size;
    this._evict();

    return data;
  }

  /**
   * Trigger a background refresh for stale data.
   * Does not block the caller; silently updates the cache on success.
   * @private
   */
  _backgroundRefresh(key, collection, operation, params, fetchFn, options) {
    // Don't start a background refresh if one is already in-flight for this key
    if (this._inFlight.has(key)) return;

    const promise = this._fetchAndStore(key, collection, operation, params, fetchFn, options)
      .catch(err => {
        // Silent failure — keep existing stale data; log warning
        console.warn(`[DataCache] Background refresh failed for ${key}:`, err.message);
      })
      .finally(() => {
        this._inFlight.delete(key);
      });

    this._inFlight.set(key, {
      promise,
      callerCount: 0,
      startedAt: Date.now()
    });
  }

  /**
   * Peek at cached data without triggering a fetch or updating access time.
   * Returns the cached data if available and not expired, or null if not cached.
   * Useful for progressive rendering — check what's immediately available.
   * 
   * @param {string} collection - Collection name
   * @param {string} operation - Operation type
   * @param {Object} params - Query parameters
   * @returns {any|null} Cached data or null if not available
   */
  peek(collection, operation, params) {
    this._checkDateInvalidation();
    const key = this._generateKey(collection, operation, params);
    const entry = this._cache.get(key);
    if (entry && entry.data != null) {
      // Return cached data whether fresh or stale — useful for progressive rendering
      return entry.data;
    }
    return null;
  }

  /**
   * Clear all cached data.
   */
  clear() {
    this._cache.clear();
    this._inFlight.clear();
    this._totalSize = 0;
    this._hits = 0;
    this._misses = 0;
  }

  /**
   * Clear entries for a specific collection.
   * @param {string} collection
   */
  clearCollection(collection) {
    const prefix = `${collection}:`;
    for (const [key, entry] of this._cache) {
      if (key.startsWith(prefix)) {
        this._totalSize -= entry.size;
        this._cache.delete(key);
      }
    }
  }

  /**
   * Get cache statistics for debugging.
   * @returns {{ size: number, entries: number, hitRate: number }}
   */
  stats() {
    const totalRequests = this._hits + this._misses;
    return {
      size: this._totalSize,
      entries: this._cache.size,
      hitRate: totalRequests > 0 ? this._hits / totalRequests : 0
    };
  }
}

// Export the class for testing purposes
export { DataCache, stableHash };

// Export as a singleton instance with default configuration
export default new DataCache();
